import { initAllStores } from "./cache";
import { sendRequest } from "../utils/api";
// import { enrichOrderItems, isOnline } from "./helper-function";
// import { useModal } from "../context/ConfirmModalContext";
import Hotel from "../models/Hotel";
import Order from "../models/Order";
import { padNumber } from "./string";
import OrderItem from "../models/OrderItem";
import { SERVICE_CHARGE_RATE, VAT_RATE } from "../constants";
import BookedRoom from "../models/BookedRoom";
import Booking from "../models/Booking";

export const clearBill = async (
    billType: "bookedRoom" | "order" | "bill",
    billId: string, isPaidByGuest = false
) => {
    const cache = await initAllStores();
    // const { openModal, closeModal } = useModal();

    try {
        await sendRequest("POST", "/bills/clear", { billType, billId, isPaidByGuest });

        const user = await cache.getBy("data", { id: "user" });
        const { id, userId, ...rest } = user;

        const clearedBy = {
            isPaid: true,
            clearedBy: { id: userId, ...rest },
            clearedById: userId,
        }

        if (billType === "bill" && !isPaidByGuest) {
            const orders: Order[] = await cache.getAllBy("orders", { billId });
            const bookings: Booking[] = await cache.getAllBy("bookings", { billId });

            const bookedRooms: BookedRoom[] = await cache.getAllBy(
                "bookedRooms", { bookingId: bookings[0].id }
            );

            const orderIds = orders.map(o => o.id);
            const bookRoomIds = bookedRooms.map(b => b.id);

            await Promise.all([
                cache.updateManyBy("orders", orderIds, clearedBy),
                cache.updateManyBy("bookedRooms", bookRoomIds, clearedBy),
            ]);
        } else if (billType !== "bill") {
            await cache.updateManyBy(
                `${billType}s`,
                [billId], clearedBy
            );
        }
    } catch (error: any) {
        throw error;
    }
};

export const calculateBillCharge = async (
    total: number, discount: number = 0
): Promise<{
    total: number,
    serviceCharge: number,
    vatCharge: number,
    subTotal: number,
    vatRate: number,
    serviceChargeRate: number
}> => {
    const cache = await initAllStores();

    const hotel: Hotel | null = await cache.getBy("data", { id: "hotel" });
    const vatRate = hotel?.preference?.vatRate ?? VAT_RATE;
    const serviceChargeRate = hotel?.preference?.serviceCharge ?? SERVICE_CHARGE_RATE;

    const vatCharge = total * vatRate / 100;
    const serviceCharge = total * serviceChargeRate / 100;
    const subTotal = total - vatCharge - serviceCharge - discount;

    return {
        vatCharge,
        serviceCharge,
        subTotal,
        total, vatRate,
        serviceChargeRate
    };
}


interface GuestRoomData {
    guestName: string;
    totalDiscount: number;
    totalPendingAmount: number;
    totalPaidAmount: number;
    billNumber: string;
    orders: Order[];
}

export async function getGuestRoomBillData(
    bookedRooms: any[] = []
): Promise<GuestRoomData> {
    const cache = await initAllStores();

    const roomOrders: Order[] = [];
    let guestName = "";
    let totalDiscount = 0;
    let totalPendingAmount = 0;
    let totalPaidAmount = 0;
    let billNumber = "";

    for (const roomBooking of bookedRooms) {
        // Get all orders for this room
        const orders: Order[] | null = await cache.getAllBy("orders", {
            bookedRoomId: roomBooking.id,
        });

        // Get related booking (for booking discount)
        const booking = await cache.getBy("bookings", {
            id: roomBooking.bookingId,
        });
      
        totalDiscount += roomBooking.discount ?? 0;

        // Process orders
        for (const order of orders ?? []) {
            // const orderItems = await enrichOrderItems(order?.orderItems ?? []);
            // order["orderItems"] = orderItems;
            roomOrders.push(order);

            // Calculate order totals from items
            const orderAmount = (order?.orderItems ?? []).reduce(
                (sum: number, item: OrderItem) => sum + (item.amount ?? 0),
                0
            );

            if (order.isPaid) {
                totalPaidAmount += orderAmount;
            } else {
                totalPendingAmount += orderAmount;
            }
        }

        // Include booking payment (room fee)
        if (roomBooking?.amount) {
            if (roomBooking.isPaid) {
                totalPaidAmount += roomBooking.amount;
            } else {
                totalPendingAmount += roomBooking.amount;
            }
        }

        billNumber = generatedBillNo("BL", booking?.bill?.billNo);

        guestName = roomBooking.guestName;
    }

    return {
        orders: roomOrders, billNumber,
        guestName, totalDiscount,
        totalPendingAmount, totalPaidAmount,
    };
}


/**
 * Splits a total amount of money evenly among a number of people,
 * rounding each share to the nearest specified amount (e.g. ₦100).
 * Ensures the total remains exactly equal to the original amount.
 *
 * @param {number} total - The total amount of money to be split (e.g. 10000).
 * @param {number} people - The number of people to share the amount (e.g. 6).
 * @param {number} [roundTo=100] - The rounding base (default ₦100).
 * @returns {number[]} An array of shares, where each value is rounded to `roundTo`,
 *                     and the sum equals the original total.
 *
 * @example
 * splitMoney(10000, 6, 100);
 * // → [1700, 1700, 1700, 1600, 1600, 1700]
 * // ✅ Total = 10000
 */
export const splitMoney = (total: number, people: number, roundTo: number = 100): number[] => {
    const rawShare = total / people;
    let roundedShare = Math.round(rawShare / roundTo) * roundTo;

    let totalRounded = roundedShare * people;
    let difference = total - totalRounded;

    const shares = Array(people).fill(roundedShare);

    // Adjust the rounding difference in increments of roundTo
    let adjustUnit = roundTo;
    while (difference !== 0) {
        for (let i = 0; i < people && difference !== 0; i++) {
            if (difference > 0) {
                shares[i] += adjustUnit;
                difference -= adjustUnit;
            } else if (difference < 0) {
                shares[i] -= adjustUnit;
                difference += adjustUnit;
            }
        }
    }

    return shares;
}


export const generatedBillNo = (prefix: string, billNo?: number): string => {
    if (billNo && billNo !== 0) return prefix + "-" + padNumber(billNo);
    else return prefix + "-" + Date.now().toString().slice(-6);
}

export function generateReference(
    paymentMethod: PaymentType,
    type: "booking" | "order"
): string {
    const now = new Date();

    const year = now.getFullYear().toString();
    const month = (now.getMonth() + 1).toString().padStart(2, "0"); // months are 0-indexed
    const day = now.getDate().toString().padStart(2, "0");
    const hours = now.getHours().toString().padStart(2, "0");
    const minutes = now.getMinutes().toString().padStart(2, "0");

    const dateTimePart = `${year}${month}${day}${hours}${minutes}`;

    const methodCodeMap: Record<string, string> = {
        pos: "PO5",
        cash: "CA5",
        transfer: "TR5",
    };

    const methodCode = methodCodeMap[paymentMethod] || "XX0";

    const typeCode = type === "booking" ? "BK" : "OD";

    return `${dateTimePart}-${methodCode}-${typeCode}`;
}


export function numberToWords(num: number): string {
    if (num === 0) return "zero";

    const belowTwenty = [
        "", "one", "two", "three", "four", "five", "six", "seven",
        "eight", "nine", "ten", "eleven", "twelve", "thirteen",
        "fourteen", "fifteen", "sixteen", "seventeen", "eighteen",
        "nineteen",
    ];

    const tens = [
        "", "", "twenty", "thirty", "forty", "fifty",
        "sixty", "seventy", "eighty", "ninety",
    ];

    const thousands = ["", "thousand", "million", "billion"];

    function helper(n: number): string {
        if (n === 0) return "";
        else if (n < 20) return belowTwenty[n];
        else if (n < 100)
            return tens[Math.floor(n / 10)] + (n % 10 ? "-" + belowTwenty[n % 10] : "");
        else
            return (
                belowTwenty[Math.floor(n / 100)] +
                " hundred" +
                (n % 100 ? " and " + helper(n % 100) : "")
            );
    }

    let word = "";
    let i = 0;

    while (num > 0) {
        const chunk = num % 1000;
        if (chunk > 0) {
            word =
                helper(chunk) +
                (thousands[i] ? " " + thousands[i] : "") +
                (word ? " " + word : "");
        }
        num = Math.floor(num / 1000);
        i++;
    }

    return word.trim() + " naira only";
}


export function formatNaira(amount: number) {
    return amount.toLocaleString("en-NG", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}